import { defineStore } from 'pinia'
import type { RawLocation } from 'vue-router'
import { useLanguageStore } from '@/stores/language'
import { useLocationStore } from '@/stores/location'
import { usePartnerConfigStore } from '@/stores/partnerConfig'
import { usePreferenceStore } from '@/stores/preference'

export type SetupGroupId = 'permissions' | 'legal' | 'user-information'

export interface SetupPreference {
  name: string
  input?: string
  collections?: string
  responses?: Array<{ selected?: boolean; value?: unknown }>
  [key: string]: any
}

export interface SetupItem {
  key: string
  kind: 'preference' | 'site'
  panel?: SetupPreference
}

export interface SetupGroup {
  id: SetupGroupId
  items: SetupItem[]
}

export interface SetupNavigationOptions {
  returnToSummary?: boolean
}

interface SetupStep {
  id: SetupGroupId | 'language'
  route: RawLocation
}

const GROUP_ORDER: SetupGroupId[] = ['user-information', 'legal', 'permissions']

const GROUP_COLLECTION: Record<SetupGroupId, string> = {
  'permissions': 'geo',
  'legal': 'legal',
  'user-information': 'user',
}

const isPermission = (preference: SetupPreference): boolean =>
  preference.input === 'geolocation'

const isTerms = (preference: SetupPreference): boolean =>
  preference.input === 'terms'

const isSetupPreference = (preference: SetupPreference): boolean =>
  preference.name !== 'summary' &&
  preference.name !== 'user_interface' &&
  preference.collections !== 'system'

export const preferenceIsComplete = (preference: SetupPreference): boolean =>
  Boolean(preference.responses?.some((response) => response.selected))

export const buildSetupGroups = (
  preferences: SetupPreference[] = [],
  hasSites = false,
): SetupGroup[] => {
  const permissions: SetupItem[] = []
  const legal: SetupItem[] = []
  const userInformation: SetupItem[] = []

  preferences.filter(isSetupPreference).forEach((preference) => {
    const item: SetupItem = {
      key: preference.name,
      kind: 'preference',
      panel: preference,
    }

    if (isPermission(preference)) {
      permissions.push(item)
    } else if (isTerms(preference)) {
      legal.push(item)
    } else {
      userInformation.push(item)
    }
  })

  userInformation.sort((a, b) => {
    if (a.key === 'user_id') return -1
    if (b.key === 'user_id') return 1
    return 0
  })

  if (hasSites) {
    const userIdIndex = userInformation.findIndex((item) => item.key === 'user_id')
    const siteIndex = userIdIndex >= 0 ? userIdIndex + 1 : 0
    userInformation.splice(siteIndex, 0, { key: 'site', kind: 'site' })
  }

  const itemsByGroup: Record<SetupGroupId, SetupItem[]> = {
    permissions,
    legal,
    'user-information': userInformation,
  }

  return GROUP_ORDER
    .map((id) => ({ id, items: itemsByGroup[id] }))
    .filter((group) => group.items.length > 0)
}

const routeForGroup = (groupId: SetupGroupId) => ({
  name: 'Setup',
  params: { group: groupId },
})

export const useLaunchSetupStore = defineStore('launchSetup', {
  getters: {
    configurationReady: (): boolean =>
      Array.isArray(usePreferenceStore().preferences) &&
      Boolean(usePartnerConfigStore().code) &&
      useLanguageStore().all.length > 0,

    groups(): SetupGroup[] {
      const preferences = usePreferenceStore().all as SetupPreference[]
      const rootLocations = useLocationStore().rootLocations || []
      return buildSetupGroups(preferences, rootLocations.length > 0)
    },

    onboardingSteps(): SetupStep[] {
      const steps: SetupStep[] = []
      if (useLanguageStore().all.length > 1) {
        steps.push({
          id: 'language',
          route: { name: 'Language', query: { setup: 'true' } },
        })
      }
      this.groups.forEach((group) => {
        steps.push({ id: group.id, route: routeForGroup(group.id) })
      })
      return steps
    },

    firstGroupRoute(): RawLocation {
      return this.groups.length ? routeForGroup(this.groups[0].id) : { name: 'Start' }
    },

    groupById(): (groupId: SetupGroupId | string | undefined) => SetupGroup | undefined {
      return (groupId) => this.groups.find((group) => group.id === groupId)
    },

    isGroupComplete(): (groupId: SetupGroupId) => boolean {
      return (groupId) => {
        const group = this.groups.find((candidate) => candidate.id === groupId)
        if (!group) return true

        return group.items.every((item) => item.kind === 'site'
          ? useLocationStore().selectionsComplete
          : Boolean(item.panel && preferenceIsComplete(item.panel)))
      }
    },

    firstIncompleteGroup(): SetupGroup | undefined {
      return this.groups.find((group) => !this.isGroupComplete(group.id))
    },

    requiresLanguageSetup(): boolean {
      const languageStore = useLanguageStore()

      return this.configurationReady &&
        languageStore.all.length > 1 &&
        !languageStore.initialSetupCompleted &&
        Boolean(this.firstIncompleteGroup)
    },

    isSetupComplete(): boolean {
      return this.configurationReady &&
        !this.requiresLanguageSetup &&
        !this.firstIncompleteGroup
    },

    entryRoute(): any {
      if (!this.configurationReady) return { name: 'Setup' }
      if (this.requiresLanguageSetup) {
        return { name: 'Language', query: { setup: 'true' } }
      }
      return this.firstIncompleteGroup
        ? routeForGroup(this.firstIncompleteGroup.id)
        : { name: 'Start' }
    },

    summaryCollection(): SetupPreference[] {
      return this.groups.flatMap((group) => group.items.reduce((items: SetupPreference[], item: SetupItem) => {
        if (item.kind === 'site') {
          const locationStore = useLocationStore()
          const locationSummaries = [
            locationStore.siteSummary,
            locationStore.siteLocationSummary,
          ].filter(Boolean)

          locationSummaries.forEach((locationSummary) => {
            items.push({
              ...locationSummary,
              collections: GROUP_COLLECTION[group.id],
              editRoutePath: '/setup/user-information?focus=site',
            })
          })
          return items
        }

        if (item.panel && preferenceIsComplete(item.panel)) {
          items.push({
            ...item.panel,
            collections: GROUP_COLLECTION[group.id],
            editRoutePath: `/setup/${group.id}?focus=${item.key}`,
          })
        }

        return items
      }, [] as SetupPreference[]))
    },

    userInformationCollection(): SetupPreference[] {
      return this.summaryCollection
        .filter((item) => item.collections === GROUP_COLLECTION['user-information'])
        .filter((item) => item.name !== 'site_location')
        .map((item) => ({ ...item, hideEdit: true }))
    },

  },

  actions: {
    nextRoute(groupId: SetupGroupId, options: SetupNavigationOptions = {}) {
      if (options.returnToSummary) {
        return { path: '/preference/summary', query: { setup: 'true' } }
      }

      const index = this.groups.findIndex((group) => group.id === groupId)
      const nextGroup = index >= 0 ? this.groups[index + 1] : undefined
      return nextGroup
        ? routeForGroup(nextGroup.id)
        : { name: 'Start', query: { setupCompleted: 'true' } }
    },

    previousRoute(groupId: SetupGroupId, options: { includeLanguage?: boolean } = {}) {
      const index = this.groups.findIndex((group) => group.id === groupId)
      const previousGroup = index > 0 ? this.groups[index - 1] : undefined
      if (index === 0 && options.includeLanguage && this.onboardingSteps[0]?.id === 'language') {
        return this.onboardingSteps[0].route
      }
      return previousGroup ? routeForGroup(previousGroup.id) : null
    },

  },
})
